import { Stack } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'

import Loading from '../Loading'
import ImageWrapper from './ImageWrapper'

export interface CropInfo {
  x: number
  y: number
  width: number
  height: number
  rotate: number
  scaleX: number
  scaleY: number
}

interface ImageCropProps {
  imageUrl: string
  data?: CropInfo
  parentRef?: React.RefObject<HTMLDivElement>
  width?: number
  height?: number
  onClick?: () => void
}

export const ImageCrop: React.FC<ImageCropProps> = ({
  imageUrl,
  parentRef,
  data,
  width = 290,
  height = 290,
  onClick = () => {},
}) => {
  const [parentWidth, setParentWidth] = useState(width)
  const [parentHeight, setParentHeight] = useState(height)

  useEffect(() => {
    const handleWindowResize = () => {
      setParentWidth(parentRef?.current?.clientWidth || width)
      setParentHeight(parentRef?.current?.clientWidth || height)
    }
    handleWindowResize()

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const containerStyle = {
    width: `${parentWidth}px`,
    height: `${parentHeight}px`,
    overflow: 'hidden',
    position: 'relative' as 'relative',
    borderRadius: '6px',
  }
  const imageStyle = useMemo(() => {
    if (!data) {
      return {
        width: `${parentWidth}px`,
        height: `${parentHeight}px`,
      }
    }
    const scaleFactor =
      data.width && data.height ? Math.min(parentWidth / data.width, parentHeight / data.height) : 1
    const scaledWidth = data.width * scaleFactor
    const scaledHeight = data.height * scaleFactor

    return {
      position: 'absolute' as 'absolute',
      left: `-${(scaledWidth - parentWidth) / 2 + data.x * scaleFactor}px`,
      top: `-${(scaledHeight - parentHeight) / 2 + data.y * scaleFactor}px`,
      transform: `rotate(${data.rotate}deg) scaleX(${data.scaleX}) scaleY(${data.scaleY}) scale(${scaleFactor})`,
      transformOrigin: 'top left',
    }
  }, [data, parentWidth, parentHeight])

  return (
    <Stack style={containerStyle} onClick={onClick}>
      {/* TODO: looks like imageUrl cannot be undefined or can it (param)? */}
      {!imageUrl ? (
        <Loading style={imageStyle} />
      ) : (
        <ImageWrapper
          src={imageUrl}
          alt="Cropped"
          style={imageStyle}
          width={parentWidth}
          height={parentHeight}
        />
      )}
    </Stack>
  )
}
